import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { resolveMediaUrl } from "@/lib/api";

export const Gallery = ({ photos = [] }) => {
  const [active, setActive] = useState(null);
  if (!photos.length) return null;

  return (
    <section id="gallery" className="bg-[#F8F6F0] py-20 md:py-28" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-14">
          <SectionHeading eyebrow="Through Our Lens" title="The Haven Gallery" testId="gallery" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {photos.map((p, i) => (
            <Reveal key={p.id} delay={(i % 4) * 0.06}>
              <button
                onClick={() => setActive(p)}
                className="group relative w-full h-44 sm:h-56 rounded-xl overflow-hidden focus:outline-none focus:ring-2 focus:ring-[#C5A059]"
                data-testid={`gallery-photo-${p.id}`}
              >
                <img src={resolveMediaUrl(p.url)} alt={p.caption || "Hotel photo"} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F]/70 via-transparent to-transparent" />
                {p.caption && (
                  <span className="absolute bottom-2.5 left-3 right-3 text-left text-xs font-medium text-[#F8F6F0] drop-shadow-md" data-testid={`gallery-caption-${p.id}`}>
                    {p.caption}
                  </span>
                )}
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-[#0A192F]/92 backdrop-blur-md flex items-center justify-center p-4"
            onClick={() => setActive(null)}
            data-testid="gallery-lightbox"
          >
            <button className="absolute top-5 right-5 text-[#F8F6F0] hover:text-[#C5A059] transition-colors" data-testid="gallery-lightbox-close" aria-label="Close">
              <X size={30} />
            </button>
            <motion.figure
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="max-w-4xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={resolveMediaUrl(active.url)} alt={active.caption || "Hotel photo"} className="w-full max-h-[75vh] object-contain rounded-xl" />
              {active.caption && <figcaption className="text-center text-[#F8F6F0]/80 mt-4 text-sm">{active.caption}</figcaption>}
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
